import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import {
  DRAFT_STORAGE_KEY,
  RECENT_CUSTOMER_KEY,
  type CapturedShot,
  type DamageZone,
  type ConsumerIntakeEstimateDraft,
  type RecentCustomerCache,
  type ShotKey,
  type VinDecodedVehicle,
} from "./types"

// State + reducer for Quick Estimate 2. Persists to localStorage
// after every change so the user can close the tab mid-flow and
// pick up where they left off. The wizard reads `hydrated` so it
// can show a "Resume vs Start over" banner on first arrival.
//
// previewUrl staleness: objectURLs are revoked when the tab closes,
// so we strip previewUrl from the persisted draft. On hydration,
// we re-seed previewUrl from uploadedUrl (the CDN URL is
// long-lived) so the captured-photo thumbnails still render after
// a resume.

interface ConsumerIntakeEstimateState {
  vin: string
  vehicle: VinDecodedVehicle | null
  shots: Partial<Record<ShotKey, CapturedShot>>
  closeUps: CapturedShot[]
  notes: string
  damageZones: DamageZone[]
  customer: {
    name: string
    phone: string
  }
}

type Action =
  | { type: "set-vin"; vin: string }
  | { type: "set-vehicle"; vehicle: VinDecodedVehicle | null }
  | { type: "set-shot"; key: ShotKey; shot: CapturedShot }
  | { type: "clear-shot"; key: ShotKey }
  | { type: "increment-retake"; key: ShotKey }
  | { type: "add-close-up"; shot: CapturedShot }
  | { type: "remove-close-up"; index: number }
  | { type: "set-notes"; notes: string }
  | { type: "toggle-damage-zone"; zone: DamageZone }
  | { type: "set-damage-zones"; zones: DamageZone[] }
  | { type: "set-customer"; customer: Partial<ConsumerIntakeEstimateState["customer"]> }
  | { type: "hydrate"; state: ConsumerIntakeEstimateState }
  | { type: "reset" }

const INITIAL: ConsumerIntakeEstimateState = {
  vin: "",
  vehicle: null,
  shots: {},
  closeUps: [],
  notes: "",
  damageZones: [],
  customer: { name: "", phone: "" },
}

function reducer(state: ConsumerIntakeEstimateState, action: Action): ConsumerIntakeEstimateState {
  switch (action.type) {
    case "set-vin":
      return { ...state, vin: action.vin }
    case "set-vehicle":
      return { ...state, vehicle: action.vehicle }
    case "set-shot":
      return { ...state, shots: { ...state.shots, [action.key]: action.shot } }
    case "clear-shot": {
      const next = { ...state.shots }
      delete next[action.key]
      return { ...state, shots: next }
    }
    case "increment-retake": {
      const existing = state.shots[action.key]
      if (!existing) return state
      return {
        ...state,
        shots: {
          ...state.shots,
          [action.key]: { ...existing, retakeCount: existing.retakeCount + 1 },
        },
      }
    }
    case "add-close-up":
      return { ...state, closeUps: [...state.closeUps, action.shot] }
    case "remove-close-up":
      return {
        ...state,
        closeUps: state.closeUps.filter((_, i) => i !== action.index),
      }
    case "set-notes":
      return { ...state, notes: action.notes }
    case "toggle-damage-zone": {
      const has = state.damageZones.includes(action.zone)
      return {
        ...state,
        damageZones: has
          ? state.damageZones.filter((z) => z !== action.zone)
          : [...state.damageZones, action.zone],
      }
    }
    case "set-damage-zones":
      return { ...state, damageZones: action.zones }
    case "set-customer":
      return {
        ...state,
        customer: { ...state.customer, ...action.customer },
      }
    case "hydrate":
      return action.state
    case "reset":
      return INITIAL
    default:
      return state
  }
}

// Strip transient fields before persisting. previewUrl is an
// objectURL that goes dead after the tab closes — re-seed it from
// uploadedUrl on hydration so the user still sees their photos.
function persistableShot(s: CapturedShot): CapturedShot {
  return {
    ...s,
    previewUrl: s.uploadedUrl || s.previewUrl,
  }
}

// `storageKey` lets the public consumer flow persist its draft under a
// separate key (etx_consumer_flow_draft_v1) so it never collides with the
// in-store advisor wizard's draft. Defaults to the advisor key.
export function useConsumerIntakeEstimateState(
  storageKey: string = DRAFT_STORAGE_KEY,
) {
  const [state, dispatch] = useReducer(reducer, INITIAL)

  // `hydrated` tells the wizard whether we restored from a draft
  // (so it can show the "Resume" banner) and how recently. `null`
  // means we either haven't checked yet or there was no draft.
  const [hydratedAt, setHydratedAt] = useState<number | null>(null)

  // Hydrate from localStorage once on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as ConsumerIntakeEstimateDraft
      if (parsed.v !== 2) return // shape changed; ignore the older draft
      // 14-day TTL — if the user came back later, the draft is stale.
      if (Date.now() - parsed.updatedAt > 14 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(storageKey)
        return
      }
      // Re-seed previewUrl from uploadedUrl (objectURL is dead).
      const rehydrate = (s: CapturedShot): CapturedShot => ({
        ...s,
        previewUrl: s.uploadedUrl || s.previewUrl,
      })
      const shots: Partial<Record<ShotKey, CapturedShot>> = {}
      for (const [k, v] of Object.entries(parsed.shots)) {
        if (v) shots[k as ShotKey] = rehydrate(v)
      }
      dispatch({
        type: "hydrate",
        state: {
          vin: parsed.vin,
          vehicle: parsed.vehicle,
          shots,
          closeUps: parsed.closeUps.map(rehydrate),
          notes: parsed.notes,
          damageZones: parsed.damageZones ?? [],
          customer: parsed.customer,
        },
      })
      setHydratedAt(parsed.updatedAt)
    } catch {
      // Corrupted JSON or quota errors — silent reset.
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Don't persist on the very first render — wait until the user
  // makes a change. Otherwise we'd overwrite an existing draft
  // with the empty INITIAL state on a fresh tab.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current && state === INITIAL) {
      isFirstRender.current = false
      return
    }
    isFirstRender.current = false
    try {
      const draft: ConsumerIntakeEstimateDraft = {
        v: 2,
        vin: state.vin,
        vehicle: state.vehicle,
        shots: Object.fromEntries(
          Object.entries(state.shots).map(([k, v]) => [k, v && persistableShot(v)]),
        ) as Partial<Record<ShotKey, CapturedShot>>,
        closeUps: state.closeUps.map(persistableShot),
        notes: state.notes,
        damageZones: state.damageZones,
        customer: state.customer,
        updatedAt: Date.now(),
      }
      localStorage.setItem(storageKey, JSON.stringify(draft))
    } catch {
      // Quota exceeded / disabled storage — proceed without persist.
    }
  }, [state, storageKey])

  const actions = useMemo(
    () => ({
      setVin: (vin: string) => dispatch({ type: "set-vin", vin }),
      setVehicle: (vehicle: VinDecodedVehicle | null) =>
        dispatch({ type: "set-vehicle", vehicle }),
      setShot: (key: ShotKey, shot: CapturedShot) =>
        dispatch({ type: "set-shot", key, shot }),
      clearShot: (key: ShotKey) => dispatch({ type: "clear-shot", key }),
      incrementRetake: (key: ShotKey) =>
        dispatch({ type: "increment-retake", key }),
      addCloseUp: (shot: CapturedShot) =>
        dispatch({ type: "add-close-up", shot }),
      removeCloseUp: (index: number) =>
        dispatch({ type: "remove-close-up", index }),
      setNotes: (notes: string) => dispatch({ type: "set-notes", notes }),
      toggleDamageZone: (zone: DamageZone) =>
        dispatch({ type: "toggle-damage-zone", zone }),
      setDamageZones: (zones: DamageZone[]) =>
        dispatch({ type: "set-damage-zones", zones }),
      setCustomer: (customer: Partial<ConsumerIntakeEstimateState["customer"]>) =>
        dispatch({ type: "set-customer", customer }),
      reset: () => {
        try {
          localStorage.removeItem(storageKey)
        } catch {
          // ignore
        }
        setHydratedAt(null)
        dispatch({ type: "reset" })
      },
    }),
    [storageKey],
  )

  const requiredShotCount = (Object.keys(state.shots) as ShotKey[]).length
  const allRequiredCaptured = requiredShotCount === 4
  const hasAnyShot = requiredShotCount + state.closeUps.length > 0

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // ignore
    }
    setHydratedAt(null)
  }, [storageKey])

  // Acknowledge the resume banner without throwing away the draft.
  const acknowledgeResume = useCallback(() => {
    setHydratedAt(null)
  }, [])

  return {
    state,
    actions,
    requiredShotCount,
    allRequiredCaptured,
    hasAnyShot,
    clearDraft,
    hydratedAt,
    acknowledgeResume,
  }
}

export type ConsumerIntakeEstimateActions = ReturnType<
  typeof useConsumerIntakeEstimateState
>["actions"]

/* ── Recent customer cache (one-tap re-fill) ─────────── */

export function recordRecentCustomer(name: string, phone: string) {
  if (!name.trim() && !phone.trim()) return
  try {
    const cache: RecentCustomerCache = {
      name: name.trim(),
      phone: phone.trim(),
      ts: Date.now(),
    }
    localStorage.setItem(RECENT_CUSTOMER_KEY, JSON.stringify(cache))
  } catch {
    // ignore
  }
}

export function readRecentCustomer(maxAgeMs = 30 * 60 * 1000): RecentCustomerCache | null {
  try {
    const raw = localStorage.getItem(RECENT_CUSTOMER_KEY)
    if (!raw) return null
    const cache = JSON.parse(raw) as RecentCustomerCache
    if (Date.now() - cache.ts > maxAgeMs) return null
    return cache
  } catch {
    return null
  }
}
